import { useState, useCallback } from "react";
import { saveKpiFrequencies, resetKpiFrequencies } from "./kpiFrequencyApi";
import { buildFrequencyPayload } from "./helpers";

/**
 * Owns the save lifecycle for this screen — the saving flag, the error message
 * and the post-save refresh — so the toolbar and the reset action can never
 * drift apart on how a write is reported.
 *
 * Every write carries the sites being edited, so a cadence set while locations
 * are selected lands on exactly those and nowhere else — in one request, so a
 * selection is never left half applied.
 * @param {object} params the financial year, the sites, and the post-save callback.
 * @param {number} params.fyId the financial year being edited.
 * @param {Array<{locationId: number, subLocationId: number}>} params.scopes the sites being edited.
 * @param {Function} params.onSaved called after a successful write.
 * @returns {object} `{ saving, saveError, lastResult, save, resetAll, clearResult }`.
 */
export function useSaveFrequencies({ fyId, scopes, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  /**
   * Sends every staged edit in one bulk request.
   * @param {object} pending staged edits keyed by KPI id.
   * @returns {Promise<boolean>} true when the write succeeded.
   */
  const save = useCallback(async (pending) => {
    const frequencies = buildFrequencyPayload(pending);
    if (!frequencies.length || !fyId || !scopes?.length || saving) return false;

    setSaving(true);
    setSaveError(null);
    try {
      const res = await saveKpiFrequencies(fyId, frequencies, scopes);
      if (!res?.isSuccess) {
        setSaveError(res?.message || "Could not save the frequencies.");
        return false;
      }
      setLastResult(res.data?.data || null);
      await onSaved?.();
      return true;
    } catch (e) {
      console.error(e);
      setSaveError("Could not save the frequencies.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [fyId, scopes, saving, onSaved]);

  /**
   * Clears the overrides for the sites being edited, so their KPIs fall back to
   * the financial-year default. Sites outside the selection keep theirs, and
   * with no site selected there is nothing to clear.
   * @returns {Promise<boolean>} true when the write succeeded.
   */
  const resetAll = useCallback(async () => {
    if (!fyId || !scopes?.length || saving) return false;

    setSaving(true);
    setSaveError(null);
    try {
      const res = await resetKpiFrequencies(fyId, scopes);
      if (!res?.isSuccess) {
        setSaveError(res?.message || "Could not reset the frequencies.");
        return false;
      }
      setLastResult(res.data?.data || null);
      await onSaved?.();
      return true;
    } catch (e) {
      console.error(e);
      setSaveError("Could not reset the frequencies.");
      return false;
    } finally {
      setSaving(false);
    }
  }, [fyId, scopes, saving, onSaved]);

  /** Dismisses the last save summary banner. */
  const clearResult = useCallback(() => setLastResult(null), []);

  return { saving, saveError, lastResult, save, resetAll, clearResult };
}
